import React from "react";
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from "@heroicons/react/20/solid";
import { spanishMonths } from "./utils";

const ACCENT = "#1b4d2e";

const VIEW_OPTIONS = [
  { key: "timeGridWeek", label: "Semana" },
  { key: "dayGridMonth", label: "Mes" },
  { key: "year", label: "Año" },
];

const TYPE_OPTIONS = [
  { key: "all", label: "Todos" },
  { key: "evento", label: "Evento" },
  { key: "scouting", label: "Scouting" },
  { key: "reunion", label: "Reunión" },
  { key: "bloqueo", label: "Bloqueo" },
];

const CalendarHeader = ({
  calendarView,
  setCalendarView,
  viewType,
  setViewType,
  espacios = [],
  espaciosVisibles = null,
  setEspaciosVisibles = () => {},
  espacioColor = {},
  laneMode = false,
  calendarRef,
  currentDate,
  setCurrentDate = () => {},
  timeZone,
  showYearView,
  setShowYearView,
  selectedYear,
  setSelectedYear,
  onNewEvent,
  soloLectura,
}) => {
  // En la vista de carriles no hay FullCalendar montado: el mes que se ve lo
  // lleva `currentDate`, así que las flechas lo mueven directo.
  const shiftMonth = (delta) =>
    setCurrentDate((d) => new Date(d.getFullYear(), d.getMonth() + delta, 1));

  const handlePrev = () => {
    if (showYearView) return setSelectedYear((y) => y - 1);
    if (laneMode) return shiftMonth(-1);
    if (calendarRef.current) calendarRef.current.getApi().prev();
  };

  const handleNext = () => {
    if (showYearView) return setSelectedYear((y) => y + 1);
    if (laneMode) return shiftMonth(1);
    if (calendarRef.current) calendarRef.current.getApi().next();
  };

  const handleToday = () => {
    if (showYearView) return setSelectedYear(new Date().getFullYear());
    if (laneMode) {
      const now = new Date();
      return setCurrentDate(new Date(now.getFullYear(), now.getMonth(), 1));
    }
    if (calendarRef.current) calendarRef.current.getApi().today();
  };

  const handleViewChange = (key) => {
    if (key === "year") {
      setShowYearView(true);
      return;
    }
    setShowYearView(false);
    setCalendarView(key);
    // Si FullCalendar está montado hay que avisarle; en carriles el estado
    // basta (y al salir a Semana se monta ya con la vista correcta).
    if (calendarRef.current) calendarRef.current.getApi().changeView(key);
  };

  // Build the title. currentDate is FullCalendar's currentStart: the real instant
  // of the displayed month's midnight in the venue's timezone. Read the month/year
  // IN THAT timezone rather than with getMonth()/getFullYear() (browser-local) — a
  // viewer west of the venue (e.g. US Pacific) would otherwise see the instant fall
  // on the previous day and the label roll back a whole month.
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timeZone || "America/Mexico_City",
    month: "numeric",
    year: "numeric",
  }).formatToParts(currentDate);
  const monthIdx = Number(parts.find((p) => p.type === "month").value) - 1;
  const titleYear = parts.find((p) => p.type === "year").value;
  const title = showYearView
    ? String(selectedYear)
    : `${spanishMonths[monthIdx].charAt(0).toUpperCase() + spanishMonths[monthIdx].slice(1)} ${titleYear}`;

  const activeViewKey = showYearView ? "year" : calendarView;

  return (
    <div className="w-full rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm px-4 py-3">
      <div className="flex flex-col sm:flex-row items-center gap-3">
        {/* Left: Nav arrows + title + Hoy */}
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={handlePrev}
            className="p-1.5 rounded-lg text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition"
            aria-label="Anterior"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <button
            onClick={handleNext}
            className="p-1.5 rounded-lg text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition"
            aria-label="Siguiente"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
          <h2 className="text-base font-bold text-zinc-900 dark:text-white ml-1 whitespace-nowrap">
            {title}
          </h2>
          <button
            onClick={handleToday}
            className="ml-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold text-zinc-600 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-700 hover:bg-zinc-200 dark:hover:bg-zinc-600 transition whitespace-nowrap"
          >
            Hoy
          </button>
        </div>

        {/* Right: View toggle + Filter toggle + New Event */}
        <div className="flex items-center gap-2 sm:ml-auto flex-wrap justify-center">
          {/* View mode pills */}
          <div className="flex items-center rounded-lg bg-zinc-100 dark:bg-zinc-700/50 p-0.5">
            {VIEW_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => handleViewChange(opt.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition whitespace-nowrap ${
                  activeViewKey === opt.key
                    ? "text-white shadow-sm"
                    : "text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                }`}
                style={activeViewKey === opt.key ? { backgroundColor: ACCENT } : undefined}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Type filter pills (disabled in year view) */}
          <div className={`flex items-center rounded-lg bg-zinc-100 dark:bg-zinc-700/50 p-0.5 ${showYearView ? "opacity-40 pointer-events-none" : ""}`}>
            {TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setViewType(opt.key)}
                className={`px-2.5 py-1.5 rounded-md text-xs font-semibold transition whitespace-nowrap ${
                  viewType === opt.key
                    ? "text-white shadow-sm"
                    : "text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                }`}
                style={viewType === opt.key ? { backgroundColor: ACCENT } : undefined}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* New Event button */}
          {!soloLectura && onNewEvent && (
            <button
              onClick={onNewEvent}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold text-white shadow-sm transition hover:opacity-90 whitespace-nowrap"
              style={{ backgroundColor: ACCENT }}
            >
              <PlusIcon className="h-4 w-4" />
              Nuevo Evento
            </button>
          )}
        </div>
      </div>

      {/* Leyenda de espacios. Es la clave de color de los carriles Y el
          filtro: un select con nombres sueltos no dice qué está ocupado. Clic
          aísla un espacio, clic de nuevo lo suelta. */}
      {espacios.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-zinc-100 dark:border-zinc-700/60 pt-2.5">
          <span className="mr-0.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
            {laneMode ? "Carriles" : "Espacios"}
          </span>
          <button
            type="button"
            onClick={() => setEspaciosVisibles(null)}
            className={`rounded-md px-2 py-1 text-[11px] font-semibold transition ${
              !espaciosVisibles
                ? "text-white"
                : "text-zinc-500 dark:text-zinc-400 bg-zinc-100 dark:bg-zinc-700/50 hover:text-zinc-700 dark:hover:text-zinc-200"
            }`}
            style={!espaciosVisibles ? { backgroundColor: ACCENT } : undefined}
          >
            Todos
          </button>
          {espacios.map((esp) => {
            const activo = !espaciosVisibles || espaciosVisibles.includes(esp.id);
            const color = espacioColor[esp.id] || ACCENT;
            return (
              <button
                key={esp.id}
                type="button"
                onClick={() =>
                  setEspaciosVisibles((prev) => {
                    // Sin filtro, el primer clic AÍSLA ese espacio (es lo que
                    // se quiere el 90% de las veces). Con filtro, alterna.
                    if (!prev) return [esp.id];
                    const next = prev.includes(esp.id)
                      ? prev.filter((x) => x !== esp.id)
                      : [...prev, esp.id];
                    // Quedarse sin carriles no muestra nada útil: se vuelve
                    // a "todos" en lugar de dejar el calendario en blanco.
                    return next.length === 0 ? null : next;
                  })
                }
                aria-pressed={activo}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-semibold transition ${
                  activo
                    ? "border-zinc-200 dark:border-zinc-600 text-zinc-700 dark:text-zinc-200 bg-white dark:bg-zinc-800"
                    : "border-transparent text-zinc-400 dark:text-zinc-500 bg-zinc-50 dark:bg-zinc-800/40 opacity-60"
                }`}
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-sm"
                  style={{ backgroundColor: activo ? color : "transparent", border: `1.5px solid ${color}` }}
                />
                {esp.nombre}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CalendarHeader;
